/**
 * The door. Two ways in — Google, or a link to an email address — and they
 * end at the same session, so nothing past this screen knows which was used.
 *
 * This is the sign-in half of what used to be all of `App.tsx`, lifted out
 * unchanged in behaviour: the same two calls, the same named errors from
 * `src/auth.ts`, and the same promise that asking for a link tells nobody
 * whether that address has an account.
 */

import { useCallback, useState, type FormEvent } from 'react'
import {
  describeSignInError,
  requestEmailSignIn,
  signInUrl,
  type EmailLinkState,
} from '../auth'

export function SignIn({ signInError }: { signInError: string | null }) {
  const [busy, setBusy] = useState(false)
  const [email, setEmail] = useState('')
  const [emailLink, setEmailLink] = useState<EmailLinkState | null>(null)

  const askForLink = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      setBusy(true)
      setEmailLink(null)
      setEmailLink(await requestEmailSignIn(email, window.location.pathname))
      setBusy(false)
    },
    [email],
  )

  return (
    <section className="card">
      <h2>Sign in</h2>
      {signInError && <p className="bad">{describeSignInError(signInError)}</p>}
      <p className="muted">
        FriendLoan keeps track of who paid for what, and what that leaves everybody owing.
      </p>
      <p>
        <a className="button" href={signInUrl(window.location.pathname)}>
          Sign in with Google
        </a>
      </p>
      <p className="muted">Or have a link emailed to you instead.</p>
      <form className="actions" onSubmit={askForLink}>
        <input
          type="email"
          required
          name="email"
          aria-label="Email address"
          placeholder="you@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <button type="submit" disabled={busy || email.trim() === ''}>
          Email me a link
        </button>
      </form>
      {emailLink?.status === 'ok' && (
        <p className="ok">
          A link is on its way to {emailLink.email}. It works once, and only for the next{' '}
          {emailLink.expiresInMinutes} minutes.
        </p>
      )}
      {emailLink?.status === 'error' && (
        <p className="bad">{describeSignInError(emailLink.error)}</p>
      )}
    </section>
  )
}
